import { Edit } from '@mui/icons-material';
import { Box, Chip } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid-premium';
import { tierOverrideContext } from 'pages/tierSetUp/tierSetUpResolver';
import { useState } from 'react';
import { TableDataPage } from 'shared/components';
import { GridColumns } from 'shared/types';
import { useUserPermissions } from 'shared/hooks';
import { formatUtcDateTimeToLocalDateTimeString, parseUtcDateTime } from 'shared/utility';
import { getStatusColumnColor } from 'shared/utility/dataGrid';
import { CustomerTierOverrideData } from 'types/backend';
import { ApprovalActions } from './approvalActions';
import { EditTierOverrideStatusForm } from './editTierOverrideStatusForm';

interface EditStatusDialogProps {
    open: boolean;
    row?: CustomerTierOverrideData;
}

export function TierOverrideApprovals() {
    const [editStatusDialogState, setEditStatusDialogState] = useState<EditStatusDialogProps>({
        open: false,
    });

    const { isApproveCustomerTierOverrideAllowed } = useUserPermissions();

    const openEditStatusDialog = (row: CustomerTierOverrideData) => {
        setEditStatusDialogState({
            open: true,
            row: row,
        });
    };

    const closeEditStatusDialog = () => {
        setEditStatusDialogState({
            open: false,
            row: undefined,
        });
    };
    const columnConfig: GridColumns<CustomerTierOverrideData> = [
        {
            field: 'edit',
            type: 'actions',
            hide: false,
            filterable: false,
            pinnable: false,
            width: 80,
            getActions: (params) => {
                return [
                    <GridActionsCellItem
                        key={`${params.id}-edit-tier-approval`}
                        icon={<Edit />}
                        label='Edit Status'
                        onClick={() => openEditStatusDialog(params.row)}
                        disabled={
                            !isApproveCustomerTierOverrideAllowed ||
                            params.row.approvalStatus === 'Approved' ||
                            params.row.approvalStatus === 'Rejected'
                        }
                    />,
                ];
            },
        },
        {
            headerName: 'Customer Name',
            field: 'branchname',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Account Number',
            field: 'accountnumber',
            hideable: false,
            pinnable: false,
            width: 150,
        },
        {
            headerName: 'Current Tier',
            field: 'currentTier',
            hideable: false,
            pinnable: false,
            width: 150,
        },
        {
            headerName: 'Tier Override',
            field: 'tierOverride',
            hideable: false,
            pinnable: false,
            width: 150,
        },
        {
            headerName: 'Previous Tier',
            field: 'prevTier',
            hideable: false,
            pinnable: false,
            width: 150,
        },
        {
            headerName: 'Est. Annual Volume',
            field: 'ttmpounds',
            type: 'number',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Revision Reason',
            field: 'revisionReason',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Comments',
            field: 'comments',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Status',
            field: 'approvalStatus',
            hideable: false,
            pinnable: false,
            width: 150,
            renderCell: (params) => (
                <Chip label={params.value} color={getStatusColumnColor(params.value)} variant='outlined' />
            ),
        },
        {
            headerName: 'Modified By',
            field: 'modifiedBy',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Start Date',
            field: 'effectivestart',
            hideable: false,
            pinnable: false,
            width: 200,
            valueGetter: (params) => parseUtcDateTime(params.value),
            valueFormatter: (params) => formatUtcDateTimeToLocalDateTimeString(params.value),
            type: 'dateTime',
        },
    ];
    const customToolbarChildren = [
        <Box key={'toolbar-flex-grow'} flexGrow={1} />,
        <ApprovalActions key='ApprovalActions' />,
    ];
    return (
        <>
            <TableDataPage
                title='Customer Tier Override Approvals'
                columnConfig={columnConfig}
                tableDataContext={tierOverrideContext}
                config={{
                    hideExport: true,
                    pinnedColumnsProps: {
                        pinnedColumnsStorage: 'customer_tier_override_approvals',
                        pinnedColumnsDefault: ['edit'],
                    },
                }}
                customToolbarChildren={customToolbarChildren}
            />
            <EditTierOverrideStatusForm
                row={editStatusDialogState.row}
                open={editStatusDialogState.open}
                onClose={closeEditStatusDialog}
            />
        </>
    );
}
